import RefreshButton from '@/components/RefreshButton';
import { BONUS_PAY_BASIS_OPTIONS } from '@/app/features/employee/emp-payroll';
import { DISCLOSER_KEYS, FILTER_KEYS } from '@/constants/keys.constants';
import { useAppDiscloser, useAppFilters } from '@/hooks';
import { Calendar03Icon, CirclePlusIcon, GiftIcon, UploadIcon } from '@/icons';
import { Button, DatePicker, Flex } from 'antd';
import { useState, type ReactNode } from 'react';
import CalendarIcon from '../../Dashboard/_components/Summery/_icons/CalendarIcon';
import type { BonusDrawerType } from '../BonusAddition';
import type { PayrollSegmentType } from '../EmpPayroll';
import BulkPayrollUpload from './BulkPayrollUpload';
import FilterSelect from './shared/FilterSelect';
import SearchInput from './shared/SearchInput';
import {
  ACTIVE_STATUS_OPTIONS,
  EMPLOYEE_TYPE_OPTIONS,
} from './shared/payroll.constants';

const { YearPicker, MonthPicker } = DatePicker;

/**
 * Page toolbars of the Payroll tabs, shown beside the page title. One small
 * component per tab; `Filters` picks the one for the open tab.
 */

/** Right-aligned toolbar rows. */
const Toolbar = ({ children }: { children: ReactNode }) => (
  <div className="flex flex-col items-end gap-4">{children}</div>
);

const Row = ({ children }: { children: ReactNode }) => (
  <Flex gap={16} align="center" wrap>
    {children}
  </Flex>
);

const PayrollGenerateFilters = () => {
  const [openUpload, setOpenUpload] = useState(false);
  const { handleSearch, handleFilter } = useAppFilters(
    FILTER_KEYS.PAYROLL_SALARY_SHEET,
  );
  const [, handleOpen] = useAppDiscloser(DISCLOSER_KEYS.PAYROLL_SALARY_SHEET);

  return (
    <Toolbar>
      <Row>
        <SearchInput placeholder="Search Batch" onChange={handleSearch} />

        <MonthPicker
          size="large"
          className="w-[120px]"
          showNow={false}
          placeholder="Month"
          format="MMM"
          suffixIcon={<CalendarIcon className="text-xl text-white" />}
          onChange={(date) => handleFilter('month', date?.format('MM'))}
        />

        <YearPicker
          size="large"
          className="w-[120px]"
          showNow={false}
          placeholder="Year"
          format="YYYY"
          suffixIcon={<CalendarIcon className="text-xl text-white" />}
          onChange={(date) =>
            handleFilter('year', date ? Number(date.format('YYYY')) : undefined)
          }
        />

        <RefreshButton
          endpoints={[
            'getAllPayrollBatch',
            'getFailedPayrollJobs',
          ]}
        />
        <Button
          size="large"
          className="btn-primary"
          icon={<UploadIcon />}
          onClick={() => setOpenUpload(true)}
        >
          Upload CSV
        </Button>
        <Button
          size="large"
          className="btn-primary"
          icon={<CirclePlusIcon />}
          onClick={() => handleOpen({ type: 'ADD' })}
        >
          Generate Salary
        </Button>
      </Row>

      <BulkPayrollUpload
        open={openUpload}
        handleClose={() => setOpenUpload(false)}
      />
    </Toolbar>
  );
};

const LeaveEncashmentFilters = () => {
  const { handleSearch, handleFilter } = useAppFilters(
    FILTER_KEYS.PAYROLL_LEAVE,
  );
  const [, handleOpen] = useAppDiscloser(DISCLOSER_KEYS.PAYROLL_LEAVE);

  return (
    <Toolbar>
      <Row>
        <SearchInput placeholder="Search Batch" onChange={handleSearch} />

        <DatePicker
          size="large"
          picker="year"
          className="w-40"
          placeholder="Select Year"
          suffixIcon={<Calendar03Icon size={16} />}
          onChange={(date) =>
            handleFilter('year', date ? Number(date.format('YYYY')) : undefined)
          }
        />

        <Button
          size="large"
          className="btn-primary"
          onClick={() => handleOpen({ type: 'ADD' })}
        >
          Generate Encashment
        </Button>
      </Row>
    </Toolbar>
  );
};

const BonusAdditionFilters = () => {
  const { handleFilter, handleSearch } = useAppFilters(
    FILTER_KEYS.PAYROLL_BONUS,
  );
  const [, handleOpen] = useAppDiscloser<BonusDrawerType>(
    DISCLOSER_KEYS.PAYROLL_BONUS,
  );

  return (
    <Toolbar>
      <Row>
        <SearchInput placeholder="Search title" onChange={handleSearch} />
        <FilterSelect
          width={150}
          placeholder="Employee Type"
          options={EMPLOYEE_TYPE_OPTIONS}
          onChange={(value) => handleFilter('employeeType', value)}
        />
        <FilterSelect
          width={150}
          placeholder="Pay Basis"
          options={BONUS_PAY_BASIS_OPTIONS}
          onChange={(value) => handleFilter('payBasis', value)}
        />
        <FilterSelect
          placeholder="Status"
          options={ACTIVE_STATUS_OPTIONS}
          onChange={(value) => handleFilter('status', value)}
        />
      </Row>

      {/* Policies are set up once; disbursing pays one into a salary month. */}
      <Row>
        <Button
          size="large"
          icon={<CirclePlusIcon size={18} />}
          onClick={() => handleOpen({ type: 'ADD' })}
          className="btn-primary-bordered"
        >
          New Bonus Policy
        </Button>
        <Button
          size="large"
          icon={<GiftIcon size={18} />}
          onClick={() => handleOpen({ type: 'DISBURSE' })}
          className="btn-primary h-11 rounded-md font-semibold"
        >
          Disburse Bonus
        </Button>
      </Row>
    </Toolbar>
  );
};

type Props = { type: PayrollSegmentType };

const Filters = ({ type }: Props) => {
  switch (type) {
    case 'salary-sheet-generate':
      return <PayrollGenerateFilters />;
    case 'leave-encashment':
      return <LeaveEncashmentFilters />;
    case 'bonus-addition':
      return <BonusAdditionFilters />;
    // Salary Structure & Grade and the Pay Sheet keep their controls inside
    // the tab, because those controls share state with what is below them.
    default:
      return null;
  }
};

export default Filters;
